import { useEffect } from 'react';

import type { CommunityGroupWithCommunities, GuideCommunity } from './useCommunityGroupsWithCommunities';

interface UseContextAwareNavigationParams {
  communityId: string | undefined;
  groupId: string | undefined;
  communityGroups: CommunityGroupWithCommunities[];
  loading: boolean;
  selectedCommunity: GuideCommunity | null;
  userNavigatedAway: boolean;
  setSelectedCommunity: (community: GuideCommunity | null) => void;
}

/**
 * Helper function to find community by ID
 */
function findCommunityById(communityGroups: CommunityGroupWithCommunities[], communityId: string): GuideCommunity | undefined {
  return communityGroups.flatMap(group => group.communities).find(c => c.id === communityId);
}

/**
 * Custom hook to handle context-aware navigation in the Guide
 * Automatically shows community pages when on a community-specific page
 * When on a community group page, stays in groups view (doesn't drill down)
 * Only applies if user hasn't manually navigated away
 */
export function useContextAwareNavigation({
  communityId,
  groupId,
  communityGroups,
  loading,
  selectedCommunity,
  userNavigatedAway,
  setSelectedCommunity,
}: UseContextAwareNavigationParams) {
  // eslint-disable-next-line complexity
  useEffect(() => {
    // Skip if loading or user manually navigated
    if (userNavigatedAway || loading) {
      return;
    }

    // On community group page: stay in groups view
    if (groupId) {
      if (selectedCommunity) {
        setSelectedCommunity(null);
      }

      return;
    }

    // On community page: drill down to community
    if (communityId && communityGroups.length > 0) {
      const community = findCommunityById(communityGroups, communityId);

      if (community && community.id !== selectedCommunity?.id) {
        setSelectedCommunity(community);
      }

      return;
    }

    // On other pages: reset to groups view
    if (selectedCommunity) {
      setSelectedCommunity(null);
    }
  }, [communityId, groupId, communityGroups, loading, selectedCommunity, userNavigatedAway, setSelectedCommunity]);
}
